<script lang="ts">
    import Button from '$lib/components/base/button/button.component.svelte';
    import CartItem from '$lib/components/composites/ecommerce-cart/cart-item.svelte';
    import CancelIcon from '$lib/components/foundation/icons/CancelIcon.component.svelte';
    import CartEmptyIcon from '$lib/components/foundation/icons/CartEmptyIcon.component.svelte';
    import CircleCheckBigIcon from '$lib/components/foundation/icons/CircleCheckBigIcon.component.svelte';
    import type { MinicartViewModel } from '$lib/types/ecommerce';
    import { onMount } from 'svelte';
    import { fly } from 'svelte/transition';

    export let cart: MinicartViewModel | undefined = undefined;
    if (typeof cart === 'string') cart = JSON.parse(cart);

    export let isOpen: boolean = false;
    if (typeof isOpen === 'string') isOpen = isOpen === 'true';

    $: isMobile = window.innerWidth < 640;

    const animateCart = (node: Element) => {
        return isMobile
            ? fly(node, { y: 600, duration: 300 })
            : fly(node, { x: 200, duration: 300 });
    };

    $: if (isOpen) {
        if (isMobile) {
            document.body.classList.add('overflow-hidden');

            // Enables the back button to close the cart on mobile

            history.replaceState({ ecommerceCartOpen: false }, document.title);
            history.pushState({ ecommerceCartOpen: true }, document.title);

            window.addEventListener('popstate', (e) => {
                if (e.state?.ecommerceCartOpen === false) {
                    e.preventDefault();
                    e.stopPropagation();
                    isOpen = false;
                }
            });
        }
    }

    $: if (!isOpen && isMobile) {
        document.body.classList.remove('overflow-hidden');
    }

    interface PopulateMiniCartEvent extends Event {
        detail?: MinicartViewModel;
    }

    onMount(() => {
        window.addEventListener('populateMiniCart', (e: PopulateMiniCartEvent) => {
            cart = e.detail;
        });

        window.addEventListener('openMiniCart', () => {
            isOpen = true;
        });
    });
</script>

{#if isOpen}
    <div
        class="w-full sm:w-[20.75rem] rounded-md overflow-hidden fixed max-sm:bottom-0 top-0 sm:top-24 right-0 sm:right-4 z-[1030] shadow-lg flex flex-col"
        role="dialog"
        aria-labelledby="ecommerce-cart--title"
        aria-live="polite"
        transition:animateCart>
        <h2 id="ecommerce-cart--title" class="sr-only">Varukorgen</h2>

        <!-- Header -->

        <div
            class="bg-prevent-primary-green text-white py-2 px-4 flex justify-between items-center">
            <h3 class="flex items-center">
                <CircleCheckBigIcon class="mr-2 shrink-0" />
                Tillagd i varukorgen
            </h3>

            <span class="flex items-center">
                <CartEmptyIcon class="mr-2 shrink-0" />
                ({cart?.totalCount})
            </span>

            <button title="Stäng" on:click|preventDefault={() => (isOpen = false)}>
                <CancelIcon />
            </button>
        </div>

        <!-- Content -->

        <div class="pt-6 px-4 max-sm:grow sm:max-h-[27.5rem] overflow-auto bg-white">
            {#if cart?.items}
                <ul>
                    {#each Object.entries(cart.items) as [_, cartItem]}
                        <li class="mb-8">
                            <CartItem item={cartItem} />
                        </li>
                    {/each}
                </ul>
            {/if}
        </div>

        <!-- Footer -->

        <div class="bg-bodyBg border-t-2 border-[#6B7280] border-solid py-6 px-4">
            <div class="flex justify-between items-center mb-4">
                <span>
                    <strong>Totalsumma</strong>
                    exkl.moms
                </span>
                <strong>{new Intl.NumberFormat('sv-SE').format(cart?.subTotal ?? 0)} kr</strong>
            </div>
            <Button theme="primary" href={cart?.checkoutUrl} size="full" class="justify-center">
                Lägg till deltagarinformation
            </Button>
        </div>
    </div>
{/if}
